import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/ui/Reveal";
import SEO from "../components/seo/SEO";
import {
  defaultCookiePreferences,
  getCookiePreferences,
  setCookiePreferences,
} from "../utils/cookiePreferences";
import { syncAnalytics } from "../utils/analytics";

export default function CookieSettingsPage() {
  const [cookies, setCookies] = useState(defaultCookiePreferences);

  useEffect(() => {
    setCookies(getCookiePreferences());
  }, []);

  const setField = (key, value) => {
    setCookies((prev) => {
      const next = { ...prev, [key]: value };
      setCookiePreferences(next);
      syncAnalytics();
      return next;
    });
  };

  const savePreferences = () => {
    setCookiePreferences(cookies);
    syncAnalytics();
  };

  const rejectNonEssential = () => {
    const next = { performance: false, functional: false, advertising: false };
    setCookies(next);
    setCookiePreferences(next);
    syncAnalytics();
  };

  const acceptAll = () => {
    const next = { performance: true, functional: true, advertising: true };
    setCookies(next);
    setCookiePreferences(next);
    syncAnalytics();
  };

  return (
    <>
      <SEO
        title="Cookie settings"
        description="Manage cookie preferences for the CampusGenie website."
        url="/cookies"
      />

      <section className="legal-page-hero">
        <div className="container">
          <Reveal>
            <span className="eyebrow">Privacy</span>
            <h1 className="heading-1" style={{ maxWidth: "16ch", margin: "0 auto 16px" }}>
              Cookie settings
            </h1>
            <p className="body-base" style={{ textAlign: "center", maxWidth: "560px", margin: "0 auto" }}>
              Essential cookies keep the site working. You can control optional categories below; your choices are stored
              in this browser on this device.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section section-sm section-alt">
        <div className="container" style={{ maxWidth: "640px" }}>
          <Reveal>
            <div className="cookie-pref-stack">
              <div className="cookie-pref-row cookie-pref-row-locked">
                <div>
                  <p className="heading-4" style={{ marginBottom: "4px" }}>
                    Essential cookies
                  </p>
                  <p className="body-small">Required for security and basic functionality.</p>
                </div>
                <span className="badge">Always on</span>
              </div>

              <label className="cookie-pref-row">
                <div>
                  <p className="heading-4" style={{ marginBottom: "4px" }}>
                    Performance cookies
                  </p>
                  <p className="body-small">Help us understand how visitors use the site.</p>
                </div>
                <input
                  type="checkbox"
                  className="cookie-checkbox"
                  checked={cookies.performance}
                  onChange={(e) => setField("performance", e.target.checked)}
                />
              </label>

              <label className="cookie-pref-row">
                <div>
                  <p className="heading-4" style={{ marginBottom: "4px" }}>
                    Functional cookies
                  </p>
                  <p className="body-small">Remember your choices and improve usability.</p>
                </div>
                <input
                  type="checkbox"
                  className="cookie-checkbox"
                  checked={cookies.functional}
                  onChange={(e) => setField("functional", e.target.checked)}
                />
              </label>

              <label className="cookie-pref-row">
                <div>
                  <p className="heading-4" style={{ marginBottom: "4px" }}>
                    Advertising cookies
                  </p>
                  <p className="body-small">Used to deliver relevant content where applicable.</p>
                </div>
                <input
                  type="checkbox"
                  className="cookie-checkbox"
                  checked={cookies.advertising}
                  onChange={(e) => setField("advertising", e.target.checked)}
                />
              </label>
            </div>

            <div className="cookie-pref-actions">
              <button type="button" className="btn btn-primary" onClick={savePreferences}>
                Save preferences
              </button>
              <button type="button" className="btn btn-secondary" onClick={rejectNonEssential}>
                Reject non-essential
              </button>
              <button type="button" className="btn btn-secondary" onClick={acceptAll}>
                Accept all
              </button>
            </div>

            <p className="body-small" style={{ textAlign: "center", marginTop: "var(--space-8)", maxWidth: "520px", marginLeft: "auto", marginRight: "auto" }}>
              With <strong>Performance</strong> enabled, we use Vercel Web Analytics and Google Analytics for Firebase
              to understand traffic and improve the site. <strong>Advertising</strong> controls any future ad-related
              scripts; we do not use them
              today. Read more in our{" "}
              <Link to="/privacy" className="legal-prose-link">
                Privacy Policy
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
